/**
 * =============================================================================
 * WorldBuilderView - Editor de la "base" de mundo (World Builder)
 * =============================================================================
 *
 * Herramienta DOM/Canvas (sin Phaser) que se abre desde el login. Dalmiro pinta
 * una grilla MACRO (cada celda = N tiles) eligiendo bioma, camino, ciudad,
 * densidad/tipo de vegetación y entradas de dungeon. El blueprint se autoguarda
 * en localStorage ("dev"), se puede exportar, y se puede generar + previsualizar
 * el MapData real con el generador procedural determinista.
 *
 * Ver docs/WORLD_BUILDER.md.
 */

import {
  type WorldBlueprint,
  type Biome,
  type VegetationDensity,
  BLUEPRINT_STORAGE_KEY,
  VEGETATION_MIXED,
  cellIndex,
  createEmptyBlueprint,
  createDefaultCell,
  normalizeBlueprint,
  blueprintHasContent,
} from '@shared/types/worldBlueprint';
import { generateMapFromBlueprint } from '@shared/world/worldGenerator';
import { MAP_BORDER_SIZE } from '@shared/constants';
import { listSectors, getSector } from '@shared/world/sectors';
import type { MapData } from '@shared/types/map';
import { loadMap1Raw } from '../../boot/mapData';
import { MAP1_BASE_AREAS, MAP1_BASE_NPCS } from '@shared/maps/map1BaseContent';
import { skinScrollable } from '../../ui/customScrollbar';

type ToolMode = 'biome' | 'path' | 'city' | 'dungeon' | 'dock' | 'density' | 'vegetation' | 'sector' | 'erase';

const SECTOR_COLOR = '#e67e22';

interface BiomeStyle {
  label: string;
  color: string;
}

const BIOME_STYLES: Record<Biome, BiomeStyle> = {
  grass: { label: 'Pasto', color: '#5a8f3c' },
  sand: { label: 'Arena', color: '#d9c184' },
  snow: { label: 'Nieve', color: '#e8eef2' },
  water: { label: 'Agua', color: '#3a6ea5' },
};

const PATH_COLOR = '#9c7a4d';
const CITY_COLOR = '#7a7a7a';
const DUNGEON_COLOR = '#8e44ad';
const DOCK_COLOR = '#c8873a';

const DENSITY_LABELS: Record<VegetationDensity, string> = {
  none: 'Ninguna',
  low: 'Baja',
  med: 'Media',
  high: 'Alta',
};

export interface WorldBuilderViewOptions {
  /** Contenedor donde montar la herramienta (se limpia su contenido). */
  host: HTMLElement;
  /** Callback al pulsar "Volver al login". */
  onExit: () => void;
}

export class WorldBuilderView {
  private readonly host: HTMLElement;
  private readonly onExit: () => void;

  private blueprint: WorldBlueprint;
  private tool: ToolMode = 'biome';
  private activeBiome: Biome = 'grass';
  private activeDensity: VegetationDensity = 'med';
  private activeVegetation: string = VEGETATION_MIXED;
  private brushSize = 1;

  /** ids de árboles indexados (decorations type tree), para el selector. */
  private treeIds: string[] = [];
  /** ids de decoraciones de desierto (decorations type desert), para el scatter de biomas `sand`. */
  private desertIds: string[] = [];
  /** ids de decoraciones de nieve (decorations type snow), para el scatter de biomas `snow`. */
  private snowIds: string[] = [];
  /** ids de entradas de dungeon indexadas (decorations type dungeon_entrance). */
  private entranceIds: string[] = [];
  private activeEntrance = 'entrance1';
  private entranceSelect!: HTMLSelectElement;

  /** Sector activo + radio para la herramienta de sectores. */
  private activeSector: string = listSectors()[0]?.id ?? '';
  private activeSectorRadius: number = listSectors()[0]?.defaultRadiusTiles ?? 24;
  private sectorSelect!: HTMLSelectElement;
  private sectorRadiusInput!: HTMLInputElement;

  /**
   * Crudo de map1.json (npcs/areas a preservar en un rebuild). Se fetchea en runtime (ya no se
   * bundlea); se carga en el constructor vía loadMap1Raw() (memoizado). Si aún no llegó al generar,
   * generateBlueprintMap cae grácil a MAP1_BASE_NPCS/AREAS (misma base que inyecta el server).
   */
  private map1Raw: MapData | null = null;

  private selected: { x: number; y: number } | null = null;
  private hover: { x: number; y: number } | null = null;
  private painting = false;
  private saveTimer: number | null = null;

  // --- Historial undo/redo (Ctrl+Z / Ctrl+Y) ---------------------------------
  // Se guarda el blueprint serializado en cada "commit" (fin de stroke o acción
  // discreta). `lastCommittedJson` = estado de la última confirmación; el delta
  // contra el blueprint vivo se captura como UNA entrada al commitear.
  private static readonly HISTORY_LIMIT = 50; // ≥ 10 pasos pedidos, con margen
  private undoStack: string[] = [];
  private redoStack: string[] = [];
  private lastCommittedJson = '';
  private onKeyDown?: (e: KeyboardEvent) => void;
  /** Refs a los inputs de la barra para refrescarlos tras un undo/redo. */
  private nameInputEl?: HTMLInputElement;
  private seedInputEl?: HTMLInputElement;
  private undoBtn?: HTMLButtonElement;
  private redoBtn?: HTMLButtonElement;

  /** Último mapa generado, para re-renderizar el preview en pantalla completa. */
  private lastGeneratedMap: MapData | null = null;
  private fullscreenEl: HTMLDivElement | null = null;

  private gridCanvas!: HTMLCanvasElement;
  private previewCanvas!: HTMLCanvasElement;
  private inspectorEl!: HTMLDivElement;
  private statusEl!: HTMLDivElement;
  private vegetationSelect!: HTMLSelectElement;

  constructor(options: WorldBuilderViewOptions) {
    this.host = options.host;
    this.onExit = options.onExit;
    this.blueprint = this.loadBlueprint();
    this.build();
    void this.loadAssetsIndex();
    // Crudo de map1 (npcs/areas a preservar). Fetch en runtime, memoizado.
    void loadMap1Raw().then((raw) => {
      this.map1Raw = raw;
    });
    // El blueprint durable vive en un archivo del repo (sobrevive cambios de
    // puerto/navegador, a diferencia de localStorage). Cargarlo file-first; si no
    // hay archivo todavía, respaldar lo que tengamos en localStorage.
    void this.loadBlueprintFromFile();

    // Historial: baseline = estado cargado. Atajos Ctrl+Z / Ctrl+Y (e Y/Shift+Z).
    this.lastCommittedJson = JSON.stringify(this.blueprint);
    this.updateHistoryButtons();
    this.onKeyDown = (e: KeyboardEvent): void => {
      // No pisar el undo nativo de los inputs de texto/selects.
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) {
        e.preventDefault();
        this.undo();
      } else if (k === 'y' || (k === 'z' && e.shiftKey)) {
        e.preventDefault();
        this.redo();
      }
    };
    window.addEventListener('keydown', this.onKeyDown);
    this.redrawGrid();
    this.updateInspector();
  }

  // ---------------------------------------------------------------------------
  // Persistencia
  // ---------------------------------------------------------------------------

  private loadBlueprint(): WorldBlueprint {
    try {
      const raw = window.localStorage.getItem(BLUEPRINT_STORAGE_KEY);
      if (raw) {
        return normalizeBlueprint(JSON.parse(raw));
      }
    } catch {
      // Ignorar errores de parseo/acceso.
    }
    return createEmptyBlueprint();
  }

  private scheduleSave(): void {
    if (this.saveTimer !== null) {
      window.clearTimeout(this.saveTimer);
    }
    this.saveTimer = window.setTimeout(() => {
      let local = false;
      try {
        window.localStorage.setItem(BLUEPRINT_STORAGE_KEY, JSON.stringify(this.blueprint));
        local = true;
      } catch {
        this.setStatus('No se pudo guardar en localStorage.');
      }
      // Persistir también al archivo del repo (fuente durable). Fire-and-forget:
      // el status final lo decide la respuesta del endpoint.
      void this.saveBlueprintToFile().then((file) => {
        if (file) this.setStatus('Guardado (localStorage + archivo).');
        else if (local) this.setStatus('Guardado en localStorage (archivo no disponible).');
      });
    }, 300);
  }

  /**
   * Escribe el blueprint actual al archivo del repo (shared/world/blueprint.dev.json)
   * vía el endpoint dev de Vite. Solo funciona con `npm run dev`. Devuelve true si
   * se guardó. Es la persistencia durable (localStorage es por-origen y se pierde
   * al cambiar de puerto/navegador).
   */
  private async saveBlueprintToFile(): Promise<boolean> {
    try {
      const res = await fetch('/__worldbuilder/save-blueprint', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.blueprint),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /**
   * Carga el blueprint desde el archivo del repo (file-first). Si el archivo tiene
   * contenido, gana sobre localStorage (así el dibujo es el mismo en cualquier
   * puerto/navegador). Si no hay archivo todavía pero localStorage tiene algo
   * pintado, lo respalda al archivo. Sin endpoint dev (build estático), no hace nada.
   */
  private async loadBlueprintFromFile(): Promise<void> {
    let fileBp: WorldBlueprint | null = null;
    try {
      const res = await fetch('/__worldbuilder/blueprint');
      if (res.ok) {
        fileBp = normalizeBlueprint(await res.json());
      }
    } catch {
      return; // sin endpoint dev → quedarse con lo de localStorage
    }

    if (fileBp && blueprintHasContent(fileBp)) {
      this.blueprint = fileBp;
      try {
        window.localStorage.setItem(BLUEPRINT_STORAGE_KEY, JSON.stringify(fileBp));
      } catch {
        // ignore
      }
      this.selected = null;
      // El archivo es la nueva baseline del historial (no se puede deshacer más allá).
      this.undoStack = [];
      this.redoStack = [];
      this.lastCommittedJson = JSON.stringify(this.blueprint);
      this.updateHistoryButtons();
      this.redrawGrid();
      this.updateInspector();
      if (this.seedInputEl) this.seedInputEl.value = String(this.blueprint.seed);
      if (this.nameInputEl) this.nameInputEl.value = this.blueprint.name;
      this.setStatus('Blueprint cargado desde archivo (shared/world/blueprint.dev.json).');
      return;
    }

    // No hay archivo (o está vacío): si lo de localStorage tiene contenido,
    // respaldarlo ahora para que sobreviva de acá en más.
    if (blueprintHasContent(this.blueprint)) {
      const ok = await this.saveBlueprintToFile();
      if (ok) {
        this.setStatus('Blueprint respaldado en archivo (shared/world/blueprint.dev.json).');
      }
    }
  }

  /** Handler del botón explícito de guardado a archivo. */
  private async saveBlueprintFile(): Promise<void> {
    this.setStatus('Guardando blueprint…');
    const ok = await this.saveBlueprintToFile();
    this.setStatus(
      ok
        ? '✓ Blueprint guardado en shared/world/blueprint.dev.json.'
        : '✗ No se pudo guardar (¿estás en npm run dev?). Usá "Exportar blueprint".'
    );
  }

  // ---------------------------------------------------------------------------
  // Historial undo/redo
  // ---------------------------------------------------------------------------

  /**
   * Confirma el estado actual como un paso de historial. Compara el blueprint vivo
   * contra la última confirmación; si cambió, apila el estado PREVIO en undo
   * (limitado a HISTORY_LIMIT) y limpia el redo. Idempotente si no hubo cambios.
   * Se llama al fin de cada stroke y de cada acción discreta.
   */
  private commit(): void {
    const cur = JSON.stringify(this.blueprint);
    if (cur === this.lastCommittedJson) return;
    this.undoStack.push(this.lastCommittedJson);
    if (this.undoStack.length > WorldBuilderView.HISTORY_LIMIT) {
      this.undoStack.shift();
    }
    this.redoStack = [];
    this.lastCommittedJson = cur;
    this.updateHistoryButtons();
  }

  private undo(): void {
    if (this.undoStack.length === 0) {
      this.setStatus('Nada para deshacer.');
      return;
    }
    this.redoStack.push(this.lastCommittedJson);
    const prev = this.undoStack.pop() as string;
    this.lastCommittedJson = prev;
    this.blueprint = normalizeBlueprint(JSON.parse(prev));
    this.afterHistoryRestore(`Deshecho (${this.undoStack.length} pasos restantes).`);
  }

  private redo(): void {
    if (this.redoStack.length === 0) {
      this.setStatus('Nada para rehacer.');
      return;
    }
    this.undoStack.push(this.lastCommittedJson);
    const next = this.redoStack.pop() as string;
    this.lastCommittedJson = next;
    this.blueprint = normalizeBlueprint(JSON.parse(next));
    this.afterHistoryRestore('Rehecho.');
  }

  /** Refresca toda la UI tras restaurar un estado del historial y lo persiste. */
  private afterHistoryRestore(msg: string): void {
    this.selected = null;
    this.redrawGrid();
    this.updateInspector();
    if (this.seedInputEl) this.seedInputEl.value = String(this.blueprint.seed);
    if (this.nameInputEl) this.nameInputEl.value = this.blueprint.name;
    this.updateHistoryButtons();
    // Persistir el estado restaurado (localStorage + archivo) SIN crear historial.
    try {
      window.localStorage.setItem(BLUEPRINT_STORAGE_KEY, JSON.stringify(this.blueprint));
    } catch {
      // ignore
    }
    void this.saveBlueprintToFile();
    this.setStatus(msg);
  }

  /** Habilita/deshabilita los botones de la barra según el estado de las pilas. */
  private updateHistoryButtons(): void {
    if (this.undoBtn) {
      this.undoBtn.disabled = this.undoStack.length === 0;
      this.undoBtn.style.opacity = this.undoStack.length === 0 ? '0.5' : '1';
    }
    if (this.redoBtn) {
      this.redoBtn.disabled = this.redoStack.length === 0;
      this.redoBtn.style.opacity = this.redoStack.length === 0 ? '0.5' : '1';
    }
  }

  /** Carga assets-index.json para poblar el selector de vegetación con árboles reales. */
  private async loadAssetsIndex(): Promise<void> {
    try {
      const res = await fetch('/assets/assets-index.json');
      const json = (await res.json()) as {
        decorations?: Record<string, { name: string; type: string }>;
      };
      const decos = json.decorations ?? {};
      this.treeIds = Object.entries(decos)
        .filter(([, v]) => v.type === 'tree')
        .map(([id]) => id);
      this.desertIds = Object.entries(decos)
        .filter(([, v]) => v.type === 'desert')
        .map(([id]) => id);
      this.snowIds = Object.entries(decos)
        .filter(([, v]) => v.type === 'snow')
        .map(([id]) => id);
      this.entranceIds = Object.entries(decos)
        .filter(([, v]) => v.type === 'dungeon_entrance')
        .map(([id]) => id);
      this.populateVegetationSelect();
      this.populateEntranceSelect();
    } catch {
      this.treeIds = [];
      this.desertIds = [];
      this.snowIds = [];
      this.entranceIds = [];
    }
  }

  // ---------------------------------------------------------------------------
  // Construcción del DOM
  // ---------------------------------------------------------------------------

  private build(): void {
    this.host.innerHTML = '';

    const root = document.createElement('div');
    root.style.position = 'fixed';
    root.style.inset = '0';
    root.style.display = 'flex';
    root.style.flexDirection = 'column';
    root.style.background = '#1b1b1f';
    root.style.color = '#e8e8e8';
    root.style.fontFamily = 'system-ui, sans-serif';
    root.style.fontSize = '13px';
    this.host.appendChild(root);

    root.appendChild(this.buildTopBar());

    const body = document.createElement('div');
    body.style.flex = '1';
    body.style.display = 'flex';
    body.style.minHeight = '0';
    root.appendChild(body);

    body.appendChild(this.buildLeftPanel());
    body.appendChild(this.buildCenterPanel());
    body.appendChild(this.buildRightPanel());
  }

  private buildTopBar(): HTMLElement {
    const bar = document.createElement('div');
    bar.style.display = 'flex';
    bar.style.alignItems = 'center';
    bar.style.gap = '8px';
    bar.style.padding = '8px 12px';
    bar.style.background = '#26262c';
    bar.style.borderBottom = '1px solid #000';

    const title = document.createElement('strong');
    title.textContent = '🌍 World Builder';
    title.style.marginRight = '12px';
    bar.appendChild(title);

    const nameWrap = this.labeledInput('Nombre', this.blueprint.name, (v) => {
      this.blueprint.name = v || 'dev';
      this.scheduleSave();
    });
    this.nameInputEl = nameWrap.querySelector('input') ?? undefined;
    // Commit del historial al CONFIRMAR (blur/enter), no por cada tecla.
    this.nameInputEl?.addEventListener('change', () => this.commit());
    bar.appendChild(nameWrap);

    const seedInput = this.labeledInput('Seed', String(this.blueprint.seed), (v) => {
      const n = parseInt(v, 10);
      this.blueprint.seed = Number.isFinite(n) ? n : 1;
      this.scheduleSave();
    });
    this.seedInputEl = seedInput.querySelector('input') ?? undefined;
    this.seedInputEl?.addEventListener('change', () => this.commit());
    bar.appendChild(seedInput);

    bar.appendChild(
      this.button('🎲', () => {
        // Seed pseudo-aleatoria derivada del reloj (solo UI, no afecta determinismo).
        this.blueprint.seed = Math.floor(performance.now() * 1000) % 1_000_000;
        if (this.seedInputEl) this.seedInputEl.value = String(this.blueprint.seed);
        this.scheduleSave();
        this.commit();
      })
    );

    const spacer = document.createElement('div');
    spacer.style.flex = '1';
    bar.appendChild(spacer);

    this.undoBtn = this.button('↶ Deshacer', () => this.undo());
    this.undoBtn.title = 'Deshacer (Ctrl+Z)';
    bar.appendChild(this.undoBtn);
    this.redoBtn = this.button('↷ Rehacer', () => this.redo());
    this.redoBtn.title = 'Rehacer (Ctrl+Y)';
    bar.appendChild(this.redoBtn);

    bar.appendChild(this.button('Nuevo', () => this.confirmNew()));
    bar.appendChild(this.button('Generar + Preview', () => this.generateAndPreview(), '#2d6cdf'));
    bar.appendChild(this.button('💾 Guardar en map1', () => void this.saveToMap1(), '#b5651d'));
    bar.appendChild(this.button('💾 Guardar blueprint', () => void this.saveBlueprintFile(), '#7a5cd0'));
    bar.appendChild(this.button('Exportar blueprint', () => this.exportBlueprint()));
    bar.appendChild(this.button('Exportar mapa', () => this.exportMap(), '#2e8b57'));
    bar.appendChild(this.button('Volver al login', () => this.exit()));

    return bar;
  }

  private buildLeftPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.style.width = '210px';
    panel.style.padding = '10px';
    panel.style.overflowY = 'auto';
    skinScrollable(panel);
    panel.style.background = '#222228';
    panel.style.borderRight = '1px solid #000';

    panel.appendChild(this.sectionTitle('Herramienta'));
    const tools: Array<{ mode: ToolMode; label: string }> = [
      { mode: 'biome', label: 'Bioma' },
      { mode: 'path', label: 'Camino' },
      { mode: 'city', label: 'Ciudad' },
      { mode: 'dungeon', label: 'Entrada dungeon' },
      { mode: 'dock', label: 'Muelle' },
      { mode: 'density', label: 'Densidad veg.' },
      { mode: 'vegetation', label: 'Tipo veg.' },
      { mode: 'sector', label: 'Sector' },
      { mode: 'erase', label: 'Borrar celda' },
    ];
    const toolWrap = document.createElement('div');
    toolWrap.style.display = 'flex';
    toolWrap.style.flexDirection = 'column';
    toolWrap.style.gap = '4px';
    for (const t of tools) {
      const b = this.button(t.label, () => {
        this.tool = t.mode;
        this.refreshToolButtons(toolWrap, t.mode);
      });
      b.dataset.mode = t.mode;
      b.style.textAlign = 'left';
      toolWrap.appendChild(b);
    }
    panel.appendChild(toolWrap);
    this.refreshToolButtons(toolWrap, this.tool);

    // Biomas
    panel.appendChild(this.sectionTitle('Bioma activo'));
    const biomeWrap = document.createElement('div');
    biomeWrap.style.display = 'flex';
    biomeWrap.style.flexWrap = 'wrap';
    biomeWrap.style.gap = '4px';
    (Object.keys(BIOME_STYLES) as Biome[]).forEach((b) => {
      const btn = this.button(BIOME_STYLES[b].label, () => {
        this.activeBiome = b;
        this.tool = 'biome';
        this.refreshToolButtons(toolWrap, 'biome');
        this.refreshBiomeButtons(biomeWrap);
      });
      btn.dataset.biome = b;
      const sw = document.createElement('span');
      sw.style.display = 'inline-block';
      sw.style.width = '10px';
      sw.style.height = '10px';
      sw.style.marginRight = '5px';
      sw.style.background = BIOME_STYLES[b].color;
      sw.style.border = '1px solid #000';
      btn.prepend(sw);
      biomeWrap.appendChild(btn);
    });
    panel.appendChild(biomeWrap);
    this.refreshBiomeButtons(biomeWrap);

    // Densidad
    panel.appendChild(this.sectionTitle('Densidad activa'));
    const densitySel = document.createElement('select');
    densitySel.style.width = '100%';
    (Object.keys(DENSITY_LABELS) as VegetationDensity[]).forEach((d) => {
      const opt = document.createElement('option');
      opt.value = d;
      opt.textContent = DENSITY_LABELS[d];
      densitySel.appendChild(opt);
    });
    densitySel.value = this.activeDensity;
    densitySel.onchange = () => {
      this.activeDensity = densitySel.value as VegetationDensity;
      this.tool = 'density';
      this.refreshToolButtons(toolWrap, 'density');
    };
    panel.appendChild(densitySel);

    // Vegetación
    panel.appendChild(this.sectionTitle('Tipo de vegetación'));
    this.vegetationSelect = document.createElement('select');
    this.vegetationSelect.style.width = '100%';
    this.vegetationSelect.onchange = () => {
      this.activeVegetation = this.vegetationSelect.value;
      this.tool = 'vegetation';
      this.refreshToolButtons(toolWrap, 'vegetation');
    };
    panel.appendChild(this.vegetationSelect);
    this.populateVegetationSelect();

    // Entrada de dungeon
    panel.appendChild(this.sectionTitle('Entrada de dungeon'));
    this.entranceSelect = document.createElement('select');
    this.entranceSelect.style.width = '100%';
    this.entranceSelect.onchange = () => {
      this.activeEntrance = this.entranceSelect.value;
      this.tool = 'dungeon';
      this.refreshToolButtons(toolWrap, 'dungeon');
    };
    panel.appendChild(this.entranceSelect);
    this.populateEntranceSelect();

    // Sector custom
    panel.appendChild(this.sectionTitle('Sector custom'));
    this.sectorSelect = document.createElement('select');
    this.sectorSelect.style.width = '100%';
    for (const def of listSectors()) {
      const opt = document.createElement('option');
      opt.value = def.id;
      opt.textContent = def.name;
      this.sectorSelect.appendChild(opt);
    }
    this.sectorSelect.value = this.activeSector;
    this.sectorSelect.onchange = () => {
      this.activeSector = this.sectorSelect.value;
      const def = getSector(this.activeSector);
      if (def) {
        this.activeSectorRadius = def.defaultRadiusTiles;
        this.sectorRadiusInput.value = String(this.activeSectorRadius);
      }
      this.tool = 'sector';
      this.refreshToolButtons(toolWrap, 'sector');
    };
    panel.appendChild(this.sectorSelect);

    this.sectorRadiusInput = document.createElement('input');
    this.sectorRadiusInput.type = 'number';
    this.sectorRadiusInput.min = '4';
    this.sectorRadiusInput.style.width = '100%';
    this.sectorRadiusInput.style.marginTop = '4px';
    this.sectorRadiusInput.value = String(this.activeSectorRadius);
    this.sectorRadiusInput.title = 'Radio del sector (tiles)';
    this.sectorRadiusInput.onchange = () => {
      const v = parseInt(this.sectorRadiusInput.value, 10);
      if (!Number.isNaN(v) && v > 0) this.activeSectorRadius = v;
      this.tool = 'sector';
      this.refreshToolButtons(toolWrap, 'sector');
    };
    panel.appendChild(this.sectorRadiusInput);

    // Pincel
    panel.appendChild(this.sectionTitle('Tamaño pincel'));
    const brushSel = document.createElement('select');
    brushSel.style.width = '100%';
    [1, 3, 5].forEach((n) => {
      const opt = document.createElement('option');
      opt.value = String(n);
      opt.textContent = `${n} × ${n}`;
      brushSel.appendChild(opt);
    });
    brushSel.value = String(this.brushSize);
    brushSel.onchange = () => {
      this.brushSize = parseInt(brushSel.value, 10) || 1;
    };
    panel.appendChild(brushSel);

    panel.appendChild(this.sectionTitle('Leyenda'));
    panel.appendChild(this.legend());

    return panel;
  }

  private buildCenterPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.style.flex = '1';
    panel.style.display = 'flex';
    panel.style.flexDirection = 'column';
    panel.style.alignItems = 'center';
    panel.style.justifyContent = 'center';
    panel.style.padding = '12px';
    panel.style.minWidth = '0';

    this.gridCanvas = document.createElement('canvas');
    this.gridCanvas.style.background = '#000';
    this.gridCanvas.style.imageRendering = 'pixelated';
    this.gridCanvas.style.cursor = 'crosshair';
    this.gridCanvas.style.maxWidth = '100%';
    this.gridCanvas.style.maxHeight = '100%';

    const cellPx = 22;
    this.gridCanvas.width = this.blueprint.cols * cellPx;
    this.gridCanvas.height = this.blueprint.rows * cellPx;

    this.gridCanvas.addEventListener('pointerdown', (e) => this.onPointer(e, true));
    this.gridCanvas.addEventListener('pointermove', (e) => this.onPointer(e, false));
    this.gridCanvas.addEventListener('pointerup', () => {
      // Fin de stroke: confirmar el trazo completo como UNA entrada de historial.
      if (this.painting) {
        this.painting = false;
        this.commit();
      }
    });
    this.gridCanvas.addEventListener('pointerleave', () => {
      if (this.painting) {
        this.painting = false;
        this.commit();
      }
      this.hover = null;
      this.redrawGrid();
    });
    this.gridCanvas.addEventListener('contextmenu', (e) => e.preventDefault());

    panel.appendChild(this.gridCanvas);

    this.statusEl = document.createElement('div');
    this.statusEl.style.marginTop = '8px';
    this.statusEl.style.color = '#9fd';
    this.statusEl.style.minHeight = '16px';
    panel.appendChild(this.statusEl);

    return panel;
  }

  private buildRightPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.style.width = '260px';
    panel.style.padding = '10px';
    panel.style.overflowY = 'auto';
    skinScrollable(panel);
    panel.style.background = '#222228';
    panel.style.borderLeft = '1px solid #000';

    panel.appendChild(this.sectionTitle('Celda seleccionada'));
    this.inspectorEl = document.createElement('div');
    panel.appendChild(this.inspectorEl);

    panel.appendChild(this.sectionTitle('Preview del mapa generado'));
    this.previewCanvas = document.createElement('canvas');
    this.previewCanvas.width = 240;
    this.previewCanvas.height = 240;
    this.previewCanvas.style.width = '240px';
    this.previewCanvas.style.height = '240px';
    this.previewCanvas.style.background = '#000';
    this.previewCanvas.style.imageRendering = 'pixelated';
    this.previewCanvas.style.border = '1px solid #000';
    this.previewCanvas.style.cursor = 'zoom-in';
    this.previewCanvas.title = 'Click para ver en pantalla completa';
    this.previewCanvas.onclick = () => this.openFullscreenPreview();
    panel.appendChild(this.previewCanvas);

    const fsBtn = this.button('⛶ Pantalla completa', () => this.openFullscreenPreview());
    fsBtn.style.marginTop = '6px';
    fsBtn.style.width = '100%';
    panel.appendChild(fsBtn);

    const hint = document.createElement('div');
    hint.style.marginTop = '6px';
    hint.style.color = '#aaa';
    hint.style.fontSize = '11px';
    hint.textContent =
      'El preview colorea el mapa generado (suelo, caminos y vegetación). La validación visual final es en el juego.';
    panel.appendChild(hint);

    return panel;
  }

  private populateVegetationSelect(): void {
    if (!this.vegetationSelect) return;
    const prev = this.activeVegetation;
    this.vegetationSelect.innerHTML = '';
    const mixed = document.createElement('option');
    mixed.value = VEGETATION_MIXED;
    mixed.textContent = 'Mezclada';
    this.vegetationSelect.appendChild(mixed);
    for (const id of this.treeIds) {
      const opt = document.createElement('option');
      opt.value = id;
      opt.textContent = id;
      this.vegetationSelect.appendChild(opt);
    }
    this.vegetationSelect.value = this.treeIds.includes(prev) ? prev : VEGETATION_MIXED;
    this.activeVegetation = this.vegetationSelect.value;
  }

  private populateEntranceSelect(): void {
    if (!this.entranceSelect) return;
    const prev = this.activeEntrance;
    this.entranceSelect.innerHTML = '';
    if (this.entranceIds.length === 0) {
      const opt = document.createElement('option');
      opt.value = 'entrance1';
      opt.textContent = '(sin índice)';
      this.entranceSelect.appendChild(opt);
    }
    for (const id of this.entranceIds) {
      const opt = document.createElement('option');
      opt.value = id;
      opt.textContent = id;
      this.entranceSelect.appendChild(opt);
    }
    this.entranceSelect.value = this.entranceIds.includes(prev) ? prev : this.entranceIds[0] ?? 'entrance1';
    this.activeEntrance = this.entranceSelect.value;
  }

  // ---------------------------------------------------------------------------
  // Interacción de pintado
  // ---------------------------------------------------------------------------

  private cellFromEvent(e: PointerEvent): { x: number; y: number } | null {
    const rect = this.gridCanvas.getBoundingClientRect();
    const scaleX = this.gridCanvas.width / rect.width;
    const scaleY = this.gridCanvas.height / rect.height;
    const px = (e.clientX - rect.left) * scaleX;
    const py = (e.clientY - rect.top) * scaleY;
    const cellPx = this.gridCanvas.width / this.blueprint.cols;
    const x = Math.floor(px / cellPx);
    const y = Math.floor(py / cellPx);
    if (x < 0 || y < 0 || x >= this.blueprint.cols || y >= this.blueprint.rows) {
      return null;
    }
    return { x, y };
  }

  private onPointer(e: PointerEvent, down: boolean): void {
    const cell = this.cellFromEvent(e);
    this.hover = cell;
    if (down) {
      this.painting = true;
      this.selected = cell;
    }
    if (this.painting && cell) {
      const erase = e.button === 2 || (e.buttons & 2) === 2;
      // Sector (no-borrar): se coloca SOLO en el click (down), un único placement;
      // no se re-coloca al arrastrar.
      const skipSectorDrag = this.tool === 'sector' && !erase && !down;
      if (!skipSectorDrag) {
        this.applyBrush(cell.x, cell.y, erase);
        this.updateInspector();
        this.scheduleSave();
      }
    }
    this.redrawGrid();
  }

  private applyBrush(cx: number, cy: number, eraseClick: boolean): void {
    // El sector se estampa en una sola celda (ignora el tamaño de pincel).
    if (this.tool === 'sector' && !eraseClick) {
      this.applyToCell(cx, cy, false);
      return;
    }
    const r = Math.floor(this.brushSize / 2);
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        const x = cx + dx;
        const y = cy + dy;
        if (x < 0 || y < 0 || x >= this.blueprint.cols || y >= this.blueprint.rows) continue;
        this.applyToCell(x, y, eraseClick);
      }
    }
  }

  private applyToCell(x: number, y: number, eraseClick: boolean): void {
    const idx = cellIndex(x, y, this.blueprint.cols);
    const cell = this.blueprint.cells[idx];
    if (this.tool === 'erase' || eraseClick) {
      this.blueprint.cells[idx] = createDefaultCell();
      // También quitar cualquier sector estampado en esta celda.
      if (this.blueprint.sectors) {
        this.blueprint.sectors = this.blueprint.sectors.filter(
          (s) => !(s.cellX === x && s.cellY === y)
        );
      }
      return;
    }
    switch (this.tool) {
      case 'biome':
        cell.biome = this.activeBiome;
        break;
      case 'path':
        cell.path = true;
        break;
      case 'city':
        cell.city = true;
        break;
      case 'dungeon':
        cell.dungeonEntrance = true;
        cell.dungeonEntranceSprite = this.activeEntrance;
        break;
      case 'dock':
        // Muelle: se pinta sobre una celda de AGUA (el generador busca la costa
        // más cercana y construye la pasarela). Ver docs/WATER_AND_BOATS.md.
        cell.dock = true;
        break;
      case 'density':
        cell.density = this.activeDensity;
        break;
      case 'vegetation':
        cell.vegetationType = this.activeVegetation;
        break;
      case 'sector': {
        if (!this.activeSector) break;
        if (!this.blueprint.sectors) this.blueprint.sectors = [];
        // Reemplazar si ya hay un sector en esta celda.
        this.blueprint.sectors = this.blueprint.sectors.filter(
          (s) => !(s.cellX === x && s.cellY === y)
        );
        this.blueprint.sectors.push({
          sectorId: this.activeSector,
          cellX: x,
          cellY: y,
          radiusTiles: this.activeSectorRadius,
        });
        break;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Render de la grilla
  // ---------------------------------------------------------------------------

  private redrawGrid(): void {
    const ctx = this.gridCanvas.getContext('2d');
    if (!ctx) return;
    const cols = this.blueprint.cols;
    const rows = this.blueprint.rows;
    const cellPx = this.gridCanvas.width / cols;

    ctx.clearRect(0, 0, this.gridCanvas.width, this.gridCanvas.height);

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const cell = this.blueprint.cells[cellIndex(x, y, cols)];
        const px = x * cellPx;
        const py = y * cellPx;

        ctx.fillStyle = BIOME_STYLES[cell.biome].color;
        ctx.fillRect(px, py, cellPx, cellPx);

        if (cell.city) {
          ctx.fillStyle = 'rgba(120,120,120,0.75)';
          ctx.fillRect(px, py, cellPx, cellPx);
          ctx.fillStyle = '#fff';
          ctx.font = `${Math.floor(cellPx * 0.6)}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('C', px + cellPx / 2, py + cellPx / 2);
        }

        if (cell.density !== 'none' && !cell.city) {
          const dots = cell.density === 'low' ? 1 : cell.density === 'med' ? 2 : 3;
          ctx.fillStyle = 'rgba(20,60,20,0.85)';
          for (let i = 0; i < dots; i++) {
            ctx.beginPath();
            ctx.arc(px + 4 + i * 4, py + cellPx - 4, 1.5, 0, Math.PI * 2);
            ctx.fill();
          }
        }

        if (cell.vegetationType && cell.vegetationType !== VEGETATION_MIXED && !cell.city) {
          ctx.fillStyle = '#0a3';
          ctx.fillRect(px + cellPx - 5, py + 2, 3, 3);
        }
      }
    }

    // Caminos: dibujar líneas conectando celdas path adyacentes.
    ctx.strokeStyle = PATH_COLOR;
    ctx.lineWidth = Math.max(2, cellPx * 0.18);
    ctx.lineCap = 'round';
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const cell = this.blueprint.cells[cellIndex(x, y, cols)];
        if (!cell.path) continue;
        const ccx = x * cellPx + cellPx / 2;
        const ccy = y * cellPx + cellPx / 2;
        const neighbors: Array<[number, number]> = [
          [x + 1, y],
          [x, y + 1],
        ];
        ctx.beginPath();
        ctx.arc(ccx, ccy, cellPx * 0.12, 0, Math.PI * 2);
        ctx.fillStyle = PATH_COLOR;
        ctx.fill();
        for (const [nx, ny] of neighbors) {
          if (nx >= cols || ny >= rows) continue;
          const n = this.blueprint.cells[cellIndex(nx, ny, cols)];
          if (!n.path) continue;
          ctx.beginPath();
          ctx.moveTo(ccx, ccy);
          ctx.lineTo(nx * cellPx + cellPx / 2, ny * cellPx + cellPx / 2);
          ctx.stroke();
        }
      }
    }

    // Entradas de dungeon: rombo.
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const cell = this.blueprint.cells[cellIndex(x, y, cols)];
        if (!cell.dungeonEntrance) continue;
        const ccx = x * cellPx + cellPx / 2;
        const ccy = y * cellPx + cellPx / 2;
        const s = cellPx * 0.28;
        ctx.fillStyle = DUNGEON_COLOR;
        ctx.beginPath();
        ctx.moveTo(ccx, ccy - s);
        ctx.lineTo(ccx + s, ccy);
        ctx.lineTo(ccx, ccy + s);
        ctx.lineTo(ccx - s, ccy);
        ctx.closePath();
        ctx.fill();
      }
    }

    // Muelles: cuadrado con "T" (pilote) sobre la celda.
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const cell = this.blueprint.cells[cellIndex(x, y, cols)];
        if (!cell.dock) continue;
        const ccx = x * cellPx + cellPx / 2;
        const ccy = y * cellPx + cellPx / 2;
        const s = cellPx * 0.24;
        ctx.fillStyle = DOCK_COLOR;
        ctx.fillRect(ccx - s, ccy - s * 0.5, s * 2, s);
        ctx.fillRect(ccx - s * 0.25, ccy - s * 0.5, s * 0.5, s * 1.6);
      }
    }

    // Sectores custom: círculo del radio + etiqueta del nombre.
    if (this.blueprint.sectors) {
      const tilesPerCell = this.blueprint.cellSize;
      for (const s of this.blueprint.sectors) {
        const def = getSector(s.sectorId);
        const ccx = s.cellX * cellPx + cellPx / 2;
        const ccy = s.cellY * cellPx + cellPx / 2;
        const radiusTiles = s.radiusTiles ?? def?.defaultRadiusTiles ?? tilesPerCell;
        const radiusPx = (radiusTiles / tilesPerCell) * cellPx;
        ctx.strokeStyle = SECTOR_COLOR;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(ccx, ccy, radiusPx, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = SECTOR_COLOR;
        ctx.beginPath();
        ctx.arc(ccx, ccy, Math.max(2, cellPx * 0.18), 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.font = `${Math.max(9, Math.floor(cellPx * 0.45))}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(def?.name ?? s.sectorId, ccx, ccy - radiusPx - 2);
      }
    }

    // Grilla.
    ctx.strokeStyle = 'rgba(0,0,0,0.25)';
    ctx.lineWidth = 1;
    for (let x = 0; x <= cols; x++) {
      ctx.beginPath();
      ctx.moveTo(x * cellPx, 0);
      ctx.lineTo(x * cellPx, this.gridCanvas.height);
      ctx.stroke();
    }
    for (let y = 0; y <= rows; y++) {
      ctx.beginPath();
      ctx.moveTo(0, y * cellPx);
      ctx.lineTo(this.gridCanvas.width, y * cellPx);
      ctx.stroke();
    }

    // Borde del mundo: el anillo exterior de celdas tiene su MITAD EXTERIOR bloqueada
    // (MAP_BORDER_SIZE tiles = ~media celda). Se dibuja en rojo sobre el lado que da al
    // borde (esquinas = unión en L). Es solo visual; la colisión la inyecta el _border.
    const borderPx = cellPx * (MAP_BORDER_SIZE / this.blueprint.cellSize);
    ctx.fillStyle = 'rgba(200,0,0,0.45)';
    for (let x = 0; x < cols; x++) {
      // fila superior / inferior
      ctx.fillRect(x * cellPx, 0, cellPx, borderPx);
      ctx.fillRect(x * cellPx, (rows - 1) * cellPx + (cellPx - borderPx), cellPx, borderPx);
    }
    for (let y = 0; y < rows; y++) {
      // columna izquierda / derecha
      ctx.fillRect(0, y * cellPx, borderPx, cellPx);
      ctx.fillRect((cols - 1) * cellPx + (cellPx - borderPx), y * cellPx, borderPx, cellPx);
    }

    // Cruz central: columna + fila del medio resaltadas (mundo impar → centro único).
    const midCol = Math.floor(cols / 2);
    const midRow = Math.floor(rows / 2);
    ctx.fillStyle = 'rgba(255,215,0,0.18)';
    ctx.fillRect(midCol * cellPx, 0, cellPx, this.gridCanvas.height);
    ctx.fillRect(0, midRow * cellPx, this.gridCanvas.width, cellPx);
    ctx.strokeStyle = 'rgba(255,215,0,0.85)';
    ctx.lineWidth = 2;
    ctx.strokeRect(midCol * cellPx, midRow * cellPx, cellPx, cellPx);

    // Hover / selección.
    if (this.hover) {
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.strokeRect(this.hover.x * cellPx, this.hover.y * cellPx, cellPx, cellPx);
    }
    if (this.selected) {
      ctx.strokeStyle = '#ffd700';
      ctx.lineWidth = 2;
      ctx.strokeRect(this.selected.x * cellPx, this.selected.y * cellPx, cellPx, cellPx);
    }
  }

  // ---------------------------------------------------------------------------
  // Inspector + acciones
  // ---------------------------------------------------------------------------

  private updateInspector(): void {
    if (!this.selected) {
      this.inspectorEl.innerHTML = '<div style="color:#888">Click en una celda.</div>';
      return;
    }
    const { x, y } = this.selected;
    const cell = this.blueprint.cells[cellIndex(x, y, this.blueprint.cols)];
    const rows: Array<[string, string]> = [
      ['Celda', `(${x}, ${y})`],
      ['Tiles', `${x * this.blueprint.cellSize}..${(x + 1) * this.blueprint.cellSize - 1}`],
      ['Bioma', BIOME_STYLES[cell.biome].label],
      ['Camino', cell.path ? 'sí' : 'no'],
      ['Ciudad', cell.city ? 'sí' : 'no'],
      ['Densidad', DENSITY_LABELS[cell.density]],
      ['Vegetación', cell.vegetationType ?? VEGETATION_MIXED],
      ['Dungeon', cell.dungeonEntrance ? (cell.dungeonEntranceSprite ?? 'sí') : 'no'],
      ['Muelle', cell.dock ? 'sí' : 'no'],
    ];
    const sectorHere = this.blueprint.sectors?.find((s) => s.cellX === x && s.cellY === y);
    if (sectorHere) {
      const def = getSector(sectorHere.sectorId);
      rows.push([
        'Sector',
        `${def?.name ?? sectorHere.sectorId} (r=${sectorHere.radiusTiles ?? def?.defaultRadiusTiles ?? '?'})`,
      ]);
    }
    this.inspectorEl.innerHTML = rows
      .map(
        ([k, v]) =>
          `<div style="display:flex;justify-content:space-between;padding:2px 0;border-bottom:1px solid #333"><span style="color:#9aa">${k}</span><span>${v}</span></div>`
      )
      .join('');
  }

  private generateBlueprintMap(): MapData {
    const map = generateMapFromBlueprint(this.blueprint, {
      treeIds: this.treeIds.length > 0 ? this.treeIds : undefined,
      desertIds: this.desertIds.length > 0 ? this.desertIds : undefined,
      snowIds: this.snowIds.length > 0 ? this.snowIds : undefined,
      entranceIds: this.entranceIds.length > 0 ? this.entranceIds : undefined,
      mapName: this.blueprint.name,
    });
    // El generador produce npcs/areas vacíos. Preservamos los del map1.json
    // actual (NPCs base: reclutadores, reyes, vendors, sacerdote; y las áreas
    // con sus reglas/dueño de facción) para que sobrevivan a un rebuild del
    // mundo. Si el map1.json bundleado quedó sin npcs, caemos a la lista base
    // canónica (MAP1_BASE_NPCS / MAP1_BASE_AREAS) — la misma que el server inyecta
    // al crear el mapa. Estos se editan a mano / con el Map Editor, NO con el
    // generador. Ver docs/WORLD_BUILDER.md y docs/FACTIONS.md.
    const preservedNpcs = this.map1Raw?.npcs;
    const preservedAreas = this.map1Raw?.areas;
    // MAP1_BASE_NPCS es AUTORITATIVO (layout de debug fijo, se aplica siempre): sus
    // templates SIEMPRE toman la posición de la base; del JSON sólo se conservan NPCs
    // cuyo template NO esté en la base. Igual que el loader del server (NPCManager).
    const baseTemplates = new Set(MAP1_BASE_NPCS.map((n) => n.templateId));
    const preservedNonBase = (preservedNpcs ?? []).filter((n) => !baseTemplates.has(n.templateId));
    map.npcs = [...MAP1_BASE_NPCS.map((n) => ({ ...n })), ...preservedNonBase.map((n) => ({ ...n }))];
    // El generador YA produjo las áreas de interior de casa (efecto de oscurecer al entrar)
    // y las áreas de MUELLE (`area_dock_*`, embarkables); las conservamos y les SUMAMOS las
    // áreas base (facción/ciudad/reglas) que se editan a mano. Filtramos de las preservadas
    // las `interior` y las `area_dock_*`: se regeneran SIEMPRE frescas desde el generador
    // (posición según casas/muelles nuevos), así no quedan viejas/duplicadas de un save
    // anterior. Las áreas embarkable hechas a mano (otros ids) sobreviven.
    const preservedBase = (preservedAreas ?? []).filter(
      (a) => !a.interior && !a.id.startsWith('area_dock_')
    );
    const baseAreas =
      preservedBase.length > 0
        ? preservedBase.map((a) => ({ ...a }))
        : MAP1_BASE_AREAS.map((a) => ({ ...a }));
    map.areas = [...(map.areas ?? []), ...baseAreas];
    return map;
  }

  private generateAndPreview(): void {
    this.setStatus('Generando…');
    const map = this.generateBlueprintMap();
    this.lastGeneratedMap = map;
    this.renderPreview(map);
    const groundCount = map.layers.find((l) => l.name === 'layer1_ground')?.tiles.length ?? 0;
    const decoCount = map.layers.find((l) => l.name === 'ground_deco_over')?.tiles.length ?? 0;
    this.setStatus(
      `Generado: ${groundCount} tiles de suelo, ${decoCount} decoraciones, ${
        map.translados?.length ?? 0
      } translados.`
    );
  }

  /** Colorea el MapData generado a resolución de tile en el canvas de preview. */
  private renderPreview(map: MapData): void {
    this.renderMapToCanvas(map, this.previewCanvas);
  }

  /** Dibuja el MapData coloreado por familia de sprite sobre un canvas dado. */
  private renderMapToCanvas(map: MapData, canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const size = this.blueprint.worldTiles;
    const scale = canvas.width / size;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const colorFor = (sprite: string): string => {
      if (sprite.startsWith('pattern2')) return BIOME_STYLES.grass.color;
      if (sprite.startsWith('pattern4')) return BIOME_STYLES.sand.color;
      if (sprite.startsWith('pattern6')) return BIOME_STYLES.snow.color;
      if (sprite.startsWith('pattern5')) return PATH_COLOR;
      if (sprite.startsWith('pattern7') || sprite.startsWith('pattern3')) return '#4a4750'; // dungeon1/2 (piedra oscura)
      if (sprite.includes('water')) return BIOME_STYLES.water.color;
      return '#444';
    };

    const ground = map.layers.find((l) => l.name === 'layer1_ground');
    if (ground) {
      for (const t of ground.tiles) {
        ctx.fillStyle = colorFor(t.sprite);
        const isBlock = t.sprite.endsWith('4x4');
        const span = isBlock ? 4 : 1;
        ctx.fillRect(t.x * scale, t.y * scale, Math.max(1, span * scale), Math.max(1, span * scale));
      }
    }

    const deco = map.layers.find((l) => l.name === 'ground_deco_over');
    if (deco) {
      ctx.fillStyle = '#163d16';
      const dot = Math.max(1, scale * 1.5);
      for (const t of deco.tiles) {
        ctx.fillRect(t.x * scale, t.y * scale, dot, dot);
      }
    }

    if (map.translados) {
      ctx.fillStyle = DUNGEON_COLOR;
      const m = Math.max(3, scale * 6);
      for (const tr of map.translados) {
        ctx.fillRect(tr.x * scale - m / 2, tr.y * scale - m / 2, m, m);
      }
    }
  }

  /** Abre un overlay a pantalla completa con el preview del último mapa generado. */
  private openFullscreenPreview(): void {
    let map = this.lastGeneratedMap;
    if (!map) {
      // Generar al vuelo si todavía no se generó.
      map = this.generateBlueprintMap();
      this.lastGeneratedMap = map;
    }
    if (this.fullscreenEl) {
      this.fullscreenEl.remove();
      this.fullscreenEl = null;
    }

    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.inset = '0';
    overlay.style.zIndex = '9999';
    overlay.style.background = 'rgba(10,10,12,0.96)';
    overlay.style.display = 'flex';
    overlay.style.flexDirection = 'column';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';

    const bar = document.createElement('div');
    bar.style.position = 'absolute';
    bar.style.top = '12px';
    bar.style.right = '12px';
    bar.style.display = 'flex';
    bar.style.gap = '8px';
    const closeBtn = this.button('✕ Cerrar (Esc)', () => this.closeFullscreenPreview());
    bar.appendChild(closeBtn);
    overlay.appendChild(bar);

    const info = document.createElement('div');
    info.style.position = 'absolute';
    info.style.top = '16px';
    info.style.left = '16px';
    info.style.color = '#cdd';
    const g = map.layers.find((l) => l.name === 'layer1_ground')?.tiles.length ?? 0;
    const d = map.layers.find((l) => l.name === 'ground_deco_over')?.tiles.length ?? 0;
    info.textContent = `${this.blueprint.name} — ${this.blueprint.worldTiles}² tiles · suelo ${g} · veg ${d} · seed ${this.blueprint.seed}`;
    overlay.appendChild(info);

    // Canvas cuadrado lo más grande posible.
    const side = Math.max(256, Math.min(window.innerWidth, window.innerHeight) - 80);
    const canvas = document.createElement('canvas');
    canvas.width = side;
    canvas.height = side;
    canvas.style.width = `${side}px`;
    canvas.style.height = `${side}px`;
    canvas.style.imageRendering = 'pixelated';
    canvas.style.border = '1px solid #000';
    canvas.style.background = '#000';
    overlay.appendChild(canvas);

    this.renderMapToCanvas(map, canvas);

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.closeFullscreenPreview();
    });

    this.host.appendChild(overlay);
    this.fullscreenEl = overlay;
    window.addEventListener('keydown', this.onFullscreenKey);
  }

  private closeFullscreenPreview(): void {
    if (this.fullscreenEl) {
      this.fullscreenEl.remove();
      this.fullscreenEl = null;
    }
    window.removeEventListener('keydown', this.onFullscreenKey);
  }

  private onFullscreenKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.closeFullscreenPreview();
  };

  private exportBlueprint(): void {
    this.download(
      `${this.blueprint.name}.blueprint.json`,
      JSON.stringify(this.blueprint, null, 2)
    );
    this.setStatus('Blueprint exportado.');
  }

  private exportMap(): void {
    const map = this.generateBlueprintMap();
    this.lastGeneratedMap = map;
    this.renderPreview(map);
    this.download(`${this.blueprint.name}.json`, JSON.stringify(map));
    this.setStatus('Mapa exportado (minificado). Reemplazá shared/maps/map1.json y reiniciá el server.');
  }

  /**
   * Guarda el mapa generado directamente en shared/maps/map1.json vía el
   * endpoint dev de Vite (solo funciona con `npm run dev`). Tras guardar hay
   * que reiniciar el server (docker restart aureum-server) para probarlo.
   */
  private async saveToMap1(): Promise<void> {
    const map = this.generateBlueprintMap();
    this.lastGeneratedMap = map;
    this.renderPreview(map);
    this.setStatus('Guardando en map1.json…');
    try {
      const res = await fetch('/__worldbuilder/save-map', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(map),
      });
      const data = (await res.json()) as { ok: boolean; bytes?: number; error?: string };
      if (res.ok && data.ok) {
        this.setStatus(
          `✓ Guardado en shared/maps/map1.json (${((data.bytes ?? 0) / 1048576).toFixed(2)} MB). El server reinicia solo (tsx watch) y el cliente recarga. Reentrá al juego.`
        );
      } else {
        this.setStatus(`✗ No se pudo guardar: ${data.error ?? res.status}. Usá "Exportar mapa".`);
      }
    } catch (err) {
      this.setStatus(
        `✗ Endpoint no disponible (¿estás en npm run dev?): ${String(err)}. Usá "Exportar mapa".`
      );
    }
  }

  private confirmNew(): void {
    if (!window.confirm('¿Crear un blueprint nuevo? Se pierde el actual (dev).')) return;
    this.blueprint = createEmptyBlueprint(this.blueprint.name, this.blueprint.worldTiles, this.blueprint.cellSize, this.blueprint.seed);
    this.selected = null;
    this.redrawGrid();
    this.updateInspector();
    this.scheduleSave();
    this.commit();
    this.setStatus('Blueprint nuevo.');
  }

  private exit(): void {
    this.closeFullscreenPreview();
    if (this.onKeyDown) {
      window.removeEventListener('keydown', this.onKeyDown);
      this.onKeyDown = undefined;
    }
    if (this.saveTimer !== null) {
      window.clearTimeout(this.saveTimer);
    }
    // Flush final: localStorage (sync) + archivo durable (best-effort).
    try {
      window.localStorage.setItem(BLUEPRINT_STORAGE_KEY, JSON.stringify(this.blueprint));
    } catch {
      // ignore
    }
    void this.saveBlueprintToFile();
    this.onExit();
  }

  // ---------------------------------------------------------------------------
  // Helpers de UI
  // ---------------------------------------------------------------------------

  private setStatus(msg: string): void {
    this.statusEl.textContent = msg;
  }

  private refreshToolButtons(wrap: HTMLElement, active: ToolMode): void {
    wrap.querySelectorAll('button').forEach((b) => {
      const el = b as HTMLButtonElement;
      el.style.outline = el.dataset.mode === active ? '2px solid #ffd700' : 'none';
    });
  }

  private refreshBiomeButtons(wrap: HTMLElement): void {
    wrap.querySelectorAll('button').forEach((b) => {
      const el = b as HTMLButtonElement;
      el.style.outline = el.dataset.biome === this.activeBiome ? '2px solid #ffd700' : 'none';
    });
  }

  private legend(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.style.fontSize = '11px';
    const items: Array<[string, string]> = [
      [BIOME_STYLES.grass.color, 'Pasto'],
      [BIOME_STYLES.sand.color, 'Arena'],
      [BIOME_STYLES.snow.color, 'Nieve'],
      [BIOME_STYLES.water.color, 'Agua'],
      [PATH_COLOR, 'Camino'],
      [CITY_COLOR, 'Ciudad (C)'],
      [DUNGEON_COLOR, 'Dungeon (rombo)'],
      [DOCK_COLOR, 'Muelle (T)'],
    ];
    for (const [color, label] of items) {
      const row = document.createElement('div');
      row.style.display = 'flex';
      row.style.alignItems = 'center';
      row.style.gap = '6px';
      row.style.padding = '1px 0';
      const sw = document.createElement('span');
      sw.style.width = '12px';
      sw.style.height = '12px';
      sw.style.background = color;
      sw.style.border = '1px solid #000';
      sw.style.display = 'inline-block';
      row.appendChild(sw);
      const t = document.createElement('span');
      t.textContent = label;
      row.appendChild(t);
      wrap.appendChild(row);
    }
    return wrap;
  }

  private sectionTitle(text: string): HTMLElement {
    const el = document.createElement('div');
    el.textContent = text;
    el.style.margin = '12px 0 6px';
    el.style.fontWeight = 'bold';
    el.style.color = '#cdd';
    el.style.fontSize = '12px';
    return el;
  }

  private button(label: string, onClick: () => void, bg = '#33333c'): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = label;
    b.style.padding = '5px 9px';
    b.style.background = bg;
    b.style.color = '#eee';
    b.style.border = '1px solid #000';
    b.style.borderRadius = '4px';
    b.style.cursor = 'pointer';
    b.onclick = onClick;
    return b;
  }

  private labeledInput(label: string, value: string, onChange: (v: string) => void): HTMLElement {
    const wrap = document.createElement('label');
    wrap.style.display = 'flex';
    wrap.style.alignItems = 'center';
    wrap.style.gap = '4px';
    const span = document.createElement('span');
    span.textContent = label;
    span.style.color = '#9aa';
    const input = document.createElement('input');
    input.value = value;
    input.style.width = label === 'Seed' ? '90px' : '120px';
    input.style.padding = '3px 5px';
    input.style.background = '#15151a';
    input.style.color = '#eee';
    input.style.border = '1px solid #000';
    input.style.borderRadius = '3px';
    input.oninput = () => onChange(input.value);
    wrap.appendChild(span);
    wrap.appendChild(input);
    return wrap;
  }

  private download(filename: string, content: string): void {
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
}
